// src/pages/session/SessionPaymentPage.js - Organized layout
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Grid,
  Divider,
  Card,
  CardContent,
  IconButton,
  Breadcrumbs,
  Link,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Checkbox,
  FormGroup,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Chip
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import PaymentIcon from '@mui/icons-material/Payment';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import BoltIcon from '@mui/icons-material/Bolt';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import { getSessionPaymentStatus, createPaymentIntentWithCustomer, savePaymentMethod, getSavedPaymentMethods } from '../../api/paymentAPI';
import { getSession } from '../../api/sessionAPI';
import { getCurrentUser as getAuthUser } from '../../api/authAPI';

// Initialize Stripe (you'll need to replace with your publishable key)
const stripePromise = loadStripe('pk_live_51RsbsBBgMwYRuzbJVTHsXY3CnXGKBPrUYijzGkNPWy54spgflhepn6mmrKrnVuxws6SxDz6UCmzg1fYaCYhz0T6Z00UBqWYh8I');

// Card element options
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: true, // Remove zip code field
  hideIcon: false, // Keep card icons but remove other elements
};

// Enhanced Payment Form Component with Card Saving
const SessionPaymentForm = ({ sessionData, paymentStatus, onPaymentSuccess, onPaymentError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  
  // New state for card saving functionality
  const [paymentMode, setPaymentMode] = useState('new'); // 'new' or 'saved'
  const [saveCard, setSaveCard] = useState(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);
  const [savedPaymentMethods, setSavedPaymentMethods] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);

  // Fetch current user and saved payment methods
  useEffect(() => {
    const fetchUserAndPaymentMethods = async () => {
      try {
        // Get current user
        const user = await getAuthUser();
        setCurrentUser(user);

        // Get saved payment methods
        const paymentMethodsResponse = await getSavedPaymentMethods();
        setSavedPaymentMethods(paymentMethodsResponse.payment_methods || []);
        
        // If user has saved methods, set default selection
        if (paymentMethodsResponse.payment_methods?.length > 0) {
          const defaultMethod = paymentMethodsResponse.payment_methods.find(pm => pm.is_default);
          if (defaultMethod) {
            setSelectedPaymentMethod(defaultMethod.payment_method_id);
            setPaymentMode('saved');
          }
        }
      } catch (error) {
        console.error('Error fetching user data:', error);
        // Continue with new card mode if there's an error
      }
    };

    fetchUserAndPaymentMethods();
  }, []);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setIsProcessing(true);
    setPaymentError(null);

    try {
      let paymentIntentData;
      let paymentMethodId = null;

      if (paymentMode === 'saved' && selectedPaymentMethod) {
        // Use saved payment method
        paymentIntentData = await createPaymentIntentWithCustomer(
          paymentStatus.session_cost,
          'nzd',
          `Payment for charging session ${sessionData.ChargeSessionId}`,
          sessionData.ChargeSessionId,
          selectedPaymentMethod,
          false // Don't save again
        );
        
        // Confirm payment with saved method
        const { error } = await stripe.confirmCardPayment(paymentIntentData.client_secret);

        if (error) {
          setPaymentError(error.message);
          onPaymentError(error.message);
          return;
        }
      } else {
        // Use new card - handle saving BEFORE payment
        const cardElement = elements.getElement(CardElement);
        
        // Create payment method first
        const { error: paymentMethodError, paymentMethod } = await stripe.createPaymentMethod({
          type: 'card',
          card: cardElement,
          billing_details: {
            name: currentUser ? `${currentUser.first_name || ''} ${currentUser.last_name || ''}`.trim() || 'Customer' : 'Customer',
            email: currentUser?.email,
          },
        });

        if (paymentMethodError) {
          setPaymentError(paymentMethodError.message);
          onPaymentError(paymentMethodError.message);
          return;
        }

        paymentMethodId = paymentMethod.id;

        // If user wants to save the card, attach it to customer BEFORE payment
        if (saveCard) {
          try {
            await savePaymentMethod(paymentMethodId, savedPaymentMethods.length === 0);
            console.log('Payment method saved successfully before payment');
          } catch (saveError) {
            console.error('Error saving payment method:', saveError);
            // Continue with payment even if saving fails
          }
        }

        // Create payment intent (without specifying payment method in creation)
        paymentIntentData = await createPaymentIntentWithCustomer(
          paymentStatus.session_cost,
          'nzd',
          `Payment for charging session ${sessionData.ChargeSessionId}`,
          sessionData.ChargeSessionId,
          null, // Don't specify payment method in creation
          false
        );

        // Confirm payment with the payment method
        const { error } = await stripe.confirmCardPayment(paymentIntentData.client_secret, {
          payment_method: paymentMethodId
        });

        if (error) {
          setPaymentError(error.message);
          onPaymentError(error.message);
          return;
        }
      }

      // Payment succeeded
      onPaymentSuccess({
        paymentIntentId: paymentIntentData.payment_intent_id,
        amount: paymentStatus.session_cost
      });

    } catch (error) {
      setPaymentError(error.message);
      onPaymentError(error.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const formatCardDisplay = (paymentMethod) => {
    return `•••• •••• •••• ${paymentMethod.card_last_four} (${paymentMethod.card_brand.toUpperCase()}) ${paymentMethod.card_exp_month}/${paymentMethod.card_exp_year}`;
  };

  return (
    <form onSubmit={handleSubmit}>
      <Box mb={3}>
        <Typography variant="h6" gutterBottom>
          Payment Details
        </Typography>

        {/* Payment Method Selection */}
        {savedPaymentMethods.length > 0 && (
          <Paper sx={{ p: 3, mb: 2, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend" sx={{ mb: 2 }}>
                Choose Payment Method
              </FormLabel>
              <RadioGroup
                value={paymentMode}
                onChange={(e) => setPaymentMode(e.target.value)}
              >
                <FormControlLabel
                  value="saved"
                  control={<Radio />}
                  label="Use Saved Card"
                />
                <FormControlLabel
                  value="new"
                  control={<Radio />}
                  label="Use New Card"
                />
              </RadioGroup>
            </FormControl>

            {/* Saved Payment Methods Selection */}
            {paymentMode === 'saved' && (
              <Box mt={2}>
                <FormControl component="fieldset" fullWidth>
                  <FormLabel component="legend" sx={{ mb: 1 }}>
                    Select Card
                  </FormLabel>
                  <List dense>
                    {savedPaymentMethods.map((pm) => (
                      <ListItem
                        key={pm.id}
                        button
                        selected={selectedPaymentMethod === pm.payment_method_id}
                        onClick={() => setSelectedPaymentMethod(pm.payment_method_id)}
                        sx={{ 
                          border: '1px solid #e0e0e0', 
                          borderRadius: 1, 
                          mb: 1,
                          backgroundColor: selectedPaymentMethod === pm.payment_method_id ? 'action.selected' : 'transparent'
                        }}
                      >
                        <ListItemText
                          primary={formatCardDisplay(pm)}
                          secondary={pm.is_default ? 'Default card' : ''}
                        />
                        <ListItemSecondaryAction>
                          {pm.is_default && (
                            <Chip label="Default" size="small" color="primary" />
                          )}
                        </ListItemSecondaryAction>
                      </ListItem>
                    ))}
                  </List>
                </FormControl>
              </Box>
            )}
          </Paper>
        )}

        {/* New Card Input */}
        {paymentMode === 'new' && (
          <Paper sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: 2 }}>
            <CardElement options={cardElementOptions} />
            
            {/* Save Card Option */}
            <FormGroup sx={{ mt: 2 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={saveCard}
                    onChange={(e) => setSaveCard(e.target.checked)}
                    color="primary"
                  />
                }
                label="Save this card for future payments"
              />
            </FormGroup>
          </Paper>
        )}
      </Box>

      {paymentError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {paymentError}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        size="large"
        fullWidth
        disabled={!stripe || isProcessing || (paymentMode === 'saved' && !selectedPaymentMethod)}
        startIcon={isProcessing ? <CircularProgress size={20} /> : <PaymentIcon />}
        sx={{ py: 1.5 }}
      >
        {isProcessing ? 'Processing Payment...' : `Pay $${paymentStatus.session_cost?.toFixed(2) || '0.00'}`}
      </Button>
    </form>
  );
};

// Format duration helper
const formatDuration = (seconds) => {
  if (!seconds) return '-';
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const remainingSeconds = seconds % 60;
  
  if (hours > 0) {
    return `${hours}h ${minutes}m ${remainingSeconds}s`;
  } else if (minutes > 0) {
    return `${minutes}m ${remainingSeconds}s`;
  } else {
    return `${remainingSeconds}s`;
  }
};

// Format date helper
const formatDateTime = (dateString) => {
  if (!dateString) return '-';
  try {
    return new Date(dateString).toLocaleString('en-GB');
  } catch {
    return dateString;
  }
};

// Main Session Payment Page Component
const SessionPaymentPage = () => {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [paymentResult, setPaymentResult] = useState(null);
  const [paymentError, setPaymentError] = useState(null);

  // Fetch session data
  const { data: sessionData, isLoading: isSessionLoading, error: sessionError } = useQuery({
    queryKey: ['session', sessionId],
    queryFn: () => getSession(parseInt(sessionId)),
    enabled: !!sessionId
  });

  // Fetch payment status
  const { data: paymentStatus, isLoading: isPaymentLoading, error: paymentStatusError } = useQuery({
    queryKey: ['session-payment-status', sessionId],
    queryFn: () => getSessionPaymentStatus(parseInt(sessionId)),
    enabled: !!sessionId
  });

  const handlePaymentSuccess = (result) => {
    setPaymentSuccess(true);
    setPaymentResult(result);
    setPaymentError(null);
  };

  const handlePaymentError = (error) => {
    setPaymentError(error);
    setPaymentSuccess(false);
  };

  const handleBackToSession = () => {
    navigate(`/sessions/${sessionId}`);
  };

  const canProcessPayment = sessionData && paymentStatus && 
                           paymentStatus.payment_required && 
                           paymentStatus.session_payment_status !== 'paid';

  // Loading state
  if (isSessionLoading || isPaymentLoading) {
    return (
      <Box display="flex" justifyContent="center" py={8}>
        <CircularProgress size={60} />
      </Box>
    );
  }

  // Error state
  if (sessionError || paymentStatusError) {
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 3 }}>
          {sessionError?.message || paymentStatusError?.message || 'Error loading session data'}
        </Alert>
        <Button onClick={handleBackToSession} startIcon={<ArrowBackIcon />}>
          Back to Session
        </Button>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header with breadcrumbs */}
      <Box display="flex" alignItems="center" mb={3}>
        <IconButton onClick={handleBackToSession} sx={{ mr: 1 }}>
          <ArrowBackIcon />
        </IconButton>
        
        <Box>
          <Typography variant="h4">
            Payment for Session #{sessionId}
          </Typography>
        </Box>
        
      </Box>

      {/* Session Summary - Single Row */}
      {sessionData && paymentStatus && (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            <BoltIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
            Session Summary
          </Typography>
          <Divider sx={{ mb: 2 }} />
          
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography variant="body2" color="text.secondary">
                Session ID
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                #{sessionData.ChargeSessionId}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography variant="body2" color="text.secondary">
                Duration
              </Typography>
              <Typography variant="body1">
                {formatDuration(sessionData.ChargerSessionDuration)}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography variant="body2" color="text.secondary">
                Energy Consumed
              </Typography>
              <Typography variant="body1">
                {sessionData.ChargerSessionEnergyKWH?.toFixed(2) || '0.00'} kWh
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={6} md={2.4}>
              <Typography variant="body2" color="text.secondary">
                Session Cost
              </Typography>
              <Typography variant="h6" color="primary.main">
                ${paymentStatus.session_cost?.toFixed(2) || '0.00'}
              </Typography>
            </Grid>
            
            <Grid item xs={12} sm={12} md={2.4}>
              <Typography variant="body2" color="text.secondary">
                Session End Time
              </Typography>
              <Typography variant="body1">
                {formatDateTime(sessionData.ChargerSessionEnd)}
              </Typography>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Payment Section */}
      {sessionData && paymentStatus && (
        <Card elevation={2}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              <PaymentIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Payment
            </Typography>
            <Divider sx={{ mb: 3 }} />

            {/* Payment Status Messages */}
            {!paymentStatus.payment_required && (
              <Alert severity="info">
                No payment required for this session
              </Alert>
            )}

            {paymentStatus.payment_required && paymentStatus.session_payment_status === 'paid' && (
              <Alert severity="success" icon={<CheckCircleIcon />}>
                This session has already been paid
              </Alert>
            )}

            {paymentSuccess && (
              <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Payment Successful!</Typography>
                <Typography>Payment Intent ID: {paymentResult?.paymentIntentId}</Typography>
                <Typography>Amount Charged: ${paymentResult?.amount?.toFixed(2)}</Typography>
              </Alert>
            )}

            {paymentError && (
              <Alert severity="error" icon={<ErrorIcon />} sx={{ mb: 2 }}>
                <Typography variant="h6" gutterBottom>Payment Failed</Typography>
                <Typography>{paymentError}</Typography>
              </Alert>
            )}

            {/* Payment Form */}
            {canProcessPayment && !paymentSuccess && (
              <Elements stripe={stripePromise}>
                <SessionPaymentForm 
                  sessionData={sessionData}
                  paymentStatus={paymentStatus}
                  onPaymentSuccess={handlePaymentSuccess}
                  onPaymentError={handlePaymentError}
                />
              </Elements>
            )}

            {/* Action Buttons */}
            <Box mt={3}>
              <Button
                variant="outlined"
                fullWidth
                onClick={handleBackToSession}
                startIcon={<ArrowBackIcon />}
              >
                Back to Session Details
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};

export default SessionPaymentPage;